import type { AppState, MealType, Recipe } from '../types';
import { daysSince, weekIdOf } from './dates';

export type SuggestSlot = { date: string; meal_type: MealType };
export type ScoredRecipe<T = unknown> = { r: Recipe; meta: T };
export type Suggestions = {
  likedRecent: { r: Recipe; lastUp: string }[];
  variety: { r: Recipe; lastUsed: string | null }[];
  overlap: { r: Recipe; score: number }[];
};

function lastUpRating(state: AppState, recipeId: string): string | null {
  let latest: string | null = null;
  for (const w of state.weeks) {
    for (const m of w.meals) {
      if (m.recipe_id === recipeId && m.rating === 'up' && m.rated_at) {
        if (!latest || m.rated_at > latest) latest = m.rated_at;
      }
    }
  }
  return latest;
}

function lastUsedDate(state: AppState, recipeId: string): string | null {
  let latest: string | null = null;
  for (const w of state.weeks) {
    for (const m of w.meals) {
      if (m.recipe_id === recipeId) {
        if (!latest || m.date > latest) latest = m.date;
      }
    }
  }
  return latest;
}

function ingredientNamesOfWeek(state: AppState, weekId: string): Set<string> {
  const week = state.weeks.find(w => w.id === weekId);
  const set = new Set<string>();
  if (!week) return set;
  for (const m of week.meals) {
    if (!m.recipe_id) continue;
    const r = state.recipes.find(x => x.id === m.recipe_id);
    if (!r) continue;
    for (const ing of r.ingredients) set.add(ing.name.trim().toLowerCase());
  }
  return set;
}

function ingredientOverlap(r: Recipe, weekIngredients: Set<string>): number {
  if (r.ingredients.length === 0) return 0;
  let hits = 0;
  for (const ing of r.ingredients) {
    if (weekIngredients.has(ing.name.trim().toLowerCase())) hits++;
  }
  return hits / r.ingredients.length;
}

export function suggestForSlot(state: AppState, slot: SuggestSlot): Suggestions {
  const t = state.settings.smart_suggest_thresholds;
  const eligible = state.recipes.filter(r =>
    r.meal_types.includes(slot.meal_type) && r.status !== 'no'
  );

  const likedRecent = eligible
    .map(r => ({ r, lastUp: lastUpRating(state, r.id) }))
    .filter((x): x is { r: Recipe; lastUp: string } =>
      x.lastUp !== null && daysSince(x.lastUp, slot.date) <= t.liked_recent_days
    )
    .sort((a, b) => daysSince(a.lastUp, slot.date) - daysSince(b.lastUp, slot.date))
    .slice(0, 8);

  const variety = eligible
    .map(r => ({ r, lastUsed: lastUsedDate(state, r.id) }))
    .filter(x => !x.lastUsed || daysSince(x.lastUsed, slot.date) >= t.variety_days)
    .sort((a, b) => {
      // prefer never-used, then oldest
      if (!a.lastUsed && b.lastUsed) return -1;
      if (a.lastUsed && !b.lastUsed) return 1;
      if (!a.lastUsed && !b.lastUsed) return 0;
      return daysSince(b.lastUsed!, slot.date) - daysSince(a.lastUsed!, slot.date);
    })
    .slice(0, 8);

  const weekIngredients = ingredientNamesOfWeek(state, weekIdOf(slot.date));
  const overlap = eligible
    .map(r => ({ r, score: ingredientOverlap(r, weekIngredients) }))
    .filter(x => x.score >= t.overlap_min)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  return { likedRecent, variety, overlap };
}

export function suggestFillForSlot(state: AppState, slot: SuggestSlot): Recipe | null {
  const s = suggestForSlot(state, slot);
  return s.overlap[0]?.r ?? s.likedRecent[0]?.r ?? s.variety[0]?.r ?? null;
}

/**
 * Fills a list of empty slots with diverse, no-repeat recipe picks.
 * Each slot gets a different recipe; categories are spread; recipes already
 * planned in the same week are excluded; archived/no-tagged recipes are excluded.
 *
 * Score per candidate (higher is better):
 *  +6 status === 'yes'      +3 status === 'maybe'
 *  +5 rated 👍 in last 30d
 *  +3 not used in 21d        +2 never used         −2 used recently
 *  +2 has photo
 *  −2× count of times this category was already picked in the current fill pass
 */
export function suggestFillsForSlots(
  state: AppState,
  slots: SuggestSlot[],
): Map<string, Recipe> {
  const result = new Map<string, Recipe>();
  if (slots.length === 0) return result;

  const usedRecipeIds = new Set<string>();
  const usedCategoryCounts = new Map<string, number>();
  const usedCuisineCounts = new Map<string, number>();

  // Seed the used set with anything already planned in any of the touched weeks
  // so a fill never duplicates an existing meal in the same week.
  const weekIds = new Set(slots.map(s => weekIdOf(s.date)));
  for (const w of state.weeks) {
    if (!weekIds.has(w.id)) continue;
    for (const m of w.meals) {
      if (m.recipe_id) {
        usedRecipeIds.add(m.recipe_id);
        const r = state.recipes.find(x => x.id === m.recipe_id);
        if (r) {
          if (r.category) {
            usedCategoryCounts.set(r.category, (usedCategoryCounts.get(r.category) ?? 0) + 1);
          }
          if (r.cuisine) {
            usedCuisineCounts.set(r.cuisine, (usedCuisineCounts.get(r.cuisine) ?? 0) + 1);
          }
        }
      }
    }
  }

  for (const slot of slots) {
    const candidates = state.recipes
      .filter(r =>
        r.meal_types.includes(slot.meal_type) &&
        r.status !== 'no' &&
        !r.archived &&
        !usedRecipeIds.has(r.id),
      )
      .map(r => ({ r, score: scoreForFill(r, state, slot, usedCategoryCounts, usedCuisineCounts) }))
      .sort((a, b) => b.score - a.score || a.r.name.localeCompare(b.r.name));

    const top = candidates[0];
    if (!top) continue;
    result.set(slotKey(slot), top.r);
    usedRecipeIds.add(top.r.id);
    if (top.r.category) {
      usedCategoryCounts.set(top.r.category, (usedCategoryCounts.get(top.r.category) ?? 0) + 1);
    }
    if (top.r.cuisine) {
      usedCuisineCounts.set(top.r.cuisine, (usedCuisineCounts.get(top.r.cuisine) ?? 0) + 1);
    }
  }
  return result;
}

export function slotKey(s: SuggestSlot): string {
  return `${s.date}:${s.meal_type}`;
}

function scoreForFill(
  r: Recipe,
  state: AppState,
  slot: SuggestSlot,
  usedCategoryCounts: Map<string, number>,
  usedCuisineCounts: Map<string, number>,
): number {
  let score = 0;
  if (r.image_url) score += 2;
  if (r.status === 'yes') score += 6;
  else if (r.status === 'maybe') score += 3;

  const lastUp = lastUpRating(state, r.id);
  if (lastUp && daysSince(lastUp, slot.date) <= 30) score += 5;

  const lastUsed = lastUsedDate(state, r.id);
  if (!lastUsed) score += 2;
  else if (daysSince(lastUsed, slot.date) >= 21) score += 3;
  else score -= 2;

  if (r.category) {
    const catCount = usedCategoryCounts.get(r.category) ?? 0;
    score -= catCount * 2;
  }
  if (r.cuisine) {
    const cuiCount = usedCuisineCounts.get(r.cuisine) ?? 0;
    score -= cuiCount * 2;
  }

  return score;
}
